import { createMemo, createSignal, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { AuditEventRow } from '../lib/sessionStream';
import { absolute } from '../lib/time';
import { AuditTimelineFilters } from './AuditTimelineFilters';
import { StatusPill, type StatusTone } from './StatusPill';

// Vertical `.timeline` rail (spec §3.13). One <details> per AuditEvent: the
// summary row is always painted (tool, seq · time, outcome pill); reasoning +
// the raw input/output payloads stay collapsed so first paint isn't blown by
// megabyte audit bodies. A client-side tool/outcome filter narrows the rail —
// view state only, the events prop stays the full stream.

export type AuditTimelineProps = {
  readonly events: readonly AuditEventRow[];
};

const OUTCOME_TONE: Record<AuditEventRow['outcome'], StatusTone> = {
  success: 'success',
  failure: 'danger',
  skipped: 'neutral',
};

const OUTCOME_LABEL: Record<AuditEventRow['outcome'], string> = {
  success: 'audit_session.outcome_success',
  failure: 'audit_session.outcome_failure',
  skipped: 'audit_session.outcome_skipped',
};

/** Copy a shareable `#event-id` deep link (#812). Best-effort: a denied
 * clipboard still navigates the hash, which is itself the permalink. */
async function copyPermalink(id: string): Promise<void> {
  const url = `${window.location.origin}${window.location.pathname}#${id}`;
  try {
    await navigator.clipboard.writeText(url);
  } catch {
    // Clipboard unavailable (permissions / insecure context) — hash still set.
  }
}

// Disclosure markers joined in the mock's retention-line voice
// ("Shown to maintainer · Redacted").
function markers(ev: AuditEventRow): string {
  return [
    ev.visible_to_maintainer ? t('audit_session.maintainer_visible') : null,
    ev.redacted ? t('audit_session.redacted') : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function AuditTimeline(props: AuditTimelineProps): JSX.Element {
  const [tool, setTool] = createSignal('');
  const [outcome, setOutcome] = createSignal('');
  const tools = createMemo(() => [...new Set(props.events.map((ev) => ev.tool))].sort());
  const filtered = createMemo(() =>
    props.events.filter(
      (ev) =>
        (tool() === '' || ev.tool === tool()) && (outcome() === '' || ev.outcome === outcome()),
    ),
  );
  return (
    <>
      <AuditTimelineFilters
        tools={tools()}
        tool={tool()}
        outcome={outcome()}
        onTool={setTool}
        onOutcome={setOutcome}
      />
      <Show when={filtered().length === 0 && props.events.length > 0}>
        <div class="list-empty">{t('audit_session.filter_empty')}</div>
      </Show>
      <ol class="timeline audit-timeline">
        <For each={filtered()}>
          {(ev) => (
            <li class="timeline__item audit-timeline__node" id={ev.id} data-outcome={ev.outcome}>
              <details class="audit-timeline__payload">
                <summary class="timeline__summary">
                  <span class="timeline__dot" aria-hidden="true" />
                  <div>
                    <p class="timeline__title">{ev.tool}</p>
                    <p class="timeline__meta">
                      #{ev.seq} ·{' '}
                      <time datetime={ev.created_at_iso}>{absolute(ev.created_at_iso)}</time>
                      {' · '}
                      <a
                        class="audit-timeline__permalink"
                        href={`#${ev.id}`}
                        title={t('audit_session.permalink')}
                        aria-label={t('audit_session.permalink')}
                        onClick={() => void copyPermalink(ev.id)}
                      >
                        #
                      </a>
                    </p>
                  </div>
                  <StatusPill tone={OUTCOME_TONE[ev.outcome]}>
                    {t(OUTCOME_LABEL[ev.outcome])}
                  </StatusPill>
                </summary>

                <div class="timeline__body">
                  <Show when={ev.reasoning}>
                    <p class="audit-timeline__reasoning">{ev.reasoning}</p>
                  </Show>
                  <p class="audit-timeline__label">{t('audit_session.input')}</p>
                  <pre class="code-block">{ev.input_json}</pre>
                  <p class="audit-timeline__label">{t('audit_session.output')}</p>
                  <pre class="code-block">{ev.output_json}</pre>
                  <Show when={markers(ev)}>
                    <p class="timeline__retention">{markers(ev)}</p>
                  </Show>
                </div>
              </details>
            </li>
          )}
        </For>
      </ol>
    </>
  );
}
